import { useId, useState, type Dispatch } from 'react'
import { SegmentedControl } from '../../platform/primitives/SegmentedControl'
import { useHitTarget } from '../../platform/responsive/hitTarget'
import { useBreakpoint } from '../../platform/responsive/useBreakpoint'
import type { Action } from './builderState'
import { QuietIconButton, RuleSelect, useControlFloor } from './ruleControls'
import {
  ANY_AMOUNT,
  FIELDS,
  FIELD_ORDER,
  amountDirection,
  conditionProblem,
  isListOp,
  type ConditionDraft,
  type ConditionProblem,
  type Direction,
  type FieldKey,
  type OpKey,
} from './ruleWire'

export const OP_LABEL: Record<OpKey, string> = {
  equals: 'equals',
  contains: 'contains',
  starts_with: 'starts with',
  ends_with: 'ends with',
  is_in: 'is in list',
  before: 'before',
  after: 'after',
  between: 'between',
  greater_than: 'is over',
  less_than: 'is under',
  greater_or_equal: 'any amount',
  not_equals: 'doesn’t equal',
  not_contains: 'doesn’t contain',
  not_in: 'isn’t in list',
}

/** An amount's operators say what they compare — "is over", not "greater than". */
const MONEY_OP_LABEL: Partial<Record<OpKey, string>> = {
  [ANY_AMOUNT]: 'any amount',
  equals: 'is exactly',
  greater_than: 'is over',
  less_than: 'is under',
  between: 'is between',
}
const opLabel = (field: FieldKey, op: OpKey) =>
  (FIELDS[field].kind === 'money' ? MONEY_OP_LABEL[op] : undefined) ?? OP_LABEL[op]

const DIRECTION_OPTIONS = [
  { value: 'spent', label: 'Money out' },
  { value: 'received', label: 'Money in' },
  { value: 'any', label: 'Either' },
] as const satisfies readonly { value: Direction; label: string }[]

const PROBLEM_TEXT: Record<ConditionProblem, string> = {
  amount_format: 'Enter an amount like 15.99',
  amount_incomplete: 'Enter both amounts',
  amount_missing: 'Enter an amount',
  amount_order: 'The first amount must be the smaller one',
}

/**
 * The money kind's value: which way the money moved, then the amount (two for
 * `between`). The amount is typed as text with a decimal keypad and checked by
 * `conditionProblem` — the same check that blocks the save — so the message and
 * the Save button never disagree. A format or "both amounts" message waits
 * until the person leaves the field ("15." is on its way to "15.99", and the
 * second box is on its way to being filled); an order message, between two
 * amounts that both parse, shows at once.
 */
function AmountValue({
  cond,
  prefix,
  minHeight,
  onDirection,
  setValue,
}: {
  cond: ConditionDraft
  prefix: string
  minHeight: number
  onDirection: (v: Direction) => void
  setValue: (v: string, second?: boolean) => void
}) {
  const [typing, setTyping] = useState(false)
  const messageId = useId()
  const problem = conditionProblem(cond)
  const shown = problem === 'amount_order' || (problem !== null && !typing) ? problem : null
  const between = cond.op === 'between'
  const input = (second: boolean, label: string) => (
    <input
      className="rule-control rule-cond__amount"
      style={{ minHeight }}
      inputMode="decimal"
      autoComplete="off"
      aria-label={`${prefix}, ${label}`}
      aria-invalid={shown !== null || undefined}
      aria-describedby={shown !== null ? messageId : undefined}
      placeholder="15.99"
      value={second ? cond.value2 : cond.value}
      onChange={(e) => setValue(e.target.value, second)}
      onFocus={() => setTyping(true)}
      onBlur={() => setTyping(false)}
    />
  )
  return (
    <>
      <SegmentedControl
        label={`${prefix}, direction`}
        name={`${prefix}-direction`}
        size="panel"
        tone="neutral"
        options={DIRECTION_OPTIONS}
        value={amountDirection(cond)}
        onChange={onDirection}
      />
      {cond.op !== ANY_AMOUNT && input(false, between ? 'lowest amount' : 'amount')}
      {between && (
        <>
          <span className="rule-cond__and" aria-hidden="true">
            and
          </span>
          {input(true, 'highest amount')}
        </>
      )}
      {shown !== null && (
        <p id={messageId} className="rule-cond__error">
          {PROBLEM_TEXT[shown]}
        </p>
      )}
    </>
  )
}

/** ISO weekdays, as the API evaluates `day_of_week`: Monday is '1'. */
export const DAYS = [
  { v: '1', l: 'Mon' }, { v: '2', l: 'Tue' }, { v: '3', l: 'Wed' }, { v: '4', l: 'Thu' },
  { v: '5', l: 'Fri' }, { v: '6', l: 'Sat' }, { v: '7', l: 'Sun' },
] as const

export type Choice = { v: string; l: string }

/** The trimmed, non-empty entries of a comma-entry list. */
const entries = (list: string[]) => list.map((s) => s.trim()).filter((s) => s !== '')

/** A comma-entry list at rest: "A, B, C". */
const shownList = (list: string[]) => entries(list).join(', ')

const sameList = (a: string[], b: string[]) => a.length === b.length && a.every((s, i) => s === b[i])

/**
 * The comma-entry input. While the user is typing it shows THEIR text,
 * untouched: a controlled input whose value is rewritten under the caret
 * throws the caret to the end, which scrambled anything typed mid-list. The
 * draft gets `text.split(',')` on every keystroke, as before.
 *
 * The tidy form ("A, B, C") is shown when the field is not being edited — on
 * blur — and whenever the draft's list stops being what this text parses to,
 * which is how a change from outside (another rule loaded into the same row,
 * the list cleared by the reducer) wins over stale text. Derived during
 * render; no effect.
 */
function ListInput({
  list,
  onList,
  minHeight,
  label,
}: {
  list: string[]
  onList: (v: string[]) => void
  minHeight: number
  label: string
}) {
  const [typed, setTyped] = useState<string | null>(null)
  const editing = typed !== null && sameList(typed.split(','), list)
  return (
    <input
      className="rule-control"
      style={{ minHeight }}
      aria-label={label}
      placeholder="Comma-separated, e.g. Safeway, Costco"
      value={editing ? typed : shownList(list)}
      onChange={(e) => {
        setTyped(e.target.value)
        onList(e.target.value.split(','))
      }}
      onBlur={() => setTyped(null)}
    />
  )
}

/**
 * One condition: field, operator, and a value control chosen by the field's
 * kind. Every control's name says which group and which condition it belongs
 * to — "Group A, condition 2, value" — because a screen-reader user tabbing
 * through three groups otherwise hears "value" nine times.
 */
export function RuleConditionRow({
  cond,
  g,
  c,
  prefix,
  removable,
  choices,
  dispatch,
}: {
  cond: ConditionDraft
  g: number
  c: number
  /** "Group A, condition 2" */
  prefix: string
  removable: boolean
  /** Options for a `select` field: accounts, or account types. */
  choices: Choice[]
  dispatch: Dispatch<Action>
}) {
  const h = useControlFloor()
  // A chip's target is its stretched <input>, which carries no `.btn` size
  // class — so it is held to the page floor (33px), not the fields' 30px.
  const chipFloor = useHitTarget('page')
  const phone = useBreakpoint() === 'phone'
  const kind = FIELDS[cond.field].kind
  const list = isListOp(cond.op)
  const setValue = (v: string, second?: boolean) => dispatch({ t: 'condValue', g, c, v, second })
  const setList = (v: string[]) => dispatch({ t: 'condList', g, c, v })

  /** Toggle chips — checkboxes for a list, radios for `equals`. The list is
   *  rebuilt in the options' own order, so Sun-then-Sat still sends '6,7'. */
  function chips(options: readonly Choice[], label: string) {
    const selected = new Set(list ? cond.list : [cond.value])
    const toggle = (v: string) => {
      if (!list) return setValue(v)
      const next = new Set(selected)
      if (next.has(v)) next.delete(v)
      else next.add(v)
      setList(options.map((o) => o.v).filter((x) => next.has(x)))
    }
    return (
      <div className="rule-chips" role="group" aria-label={`${prefix}, ${label}`}>
        {options.map((o) => {
          const on = selected.has(o.v)
          return (
            <label key={o.v} className={`rule-chip${on ? ' rule-chip--on' : ''}`} style={{ minHeight: chipFloor, minWidth: chipFloor }}>
              <input
                className="rule-chip__input"
                type={list ? 'checkbox' : 'radio'}
                name={list ? undefined : `${prefix}-${label}`}
                aria-label={`${prefix}, ${o.l}`}
                checked={on}
                onChange={() => toggle(o.v)}
              />
              {/* The mark says "on" in shape, not only in fill. */}
              {on && <span aria-hidden="true">✓ </span>}
              {o.l}
            </label>
          )
        })}
      </div>
    )
  }

  let value
  let hint = null
  if (kind === 'money') {
    value = (
      <AmountValue
        cond={cond}
        prefix={prefix}
        minHeight={h}
        onDirection={(v) => dispatch({ t: 'condDirection', g, c, v })}
        setValue={setValue}
      />
    )
  } else if (kind === 'text' && list) {
    const items = entries(cond.list)
    if (items.length > 0) {
      hint = (
        <p className="rule-cond__hint">
          {items.length} {items.length === 1 ? 'value' : 'values'}: {items.join(' · ')}
        </p>
      )
    }
    value = <ListInput list={cond.list} onList={setList} minHeight={h} label={`${prefix}, values`} />
  } else if (kind === 'text') {
    value = (
      <input
        className="rule-control"
        style={{ minHeight: h }}
        aria-label={`${prefix}, value`}
        placeholder={cond.field === 'merchant_name_raw' ? 'e.g. SQ *PRADO' : 'e.g. Amazon'}
        value={cond.value}
        onChange={(e) => setValue(e.target.value)}
      />
    )
  } else if (kind === 'select' && list) {
    value = chips(choices, 'values')
  } else if (kind === 'select') {
    const known = choices.some((o) => o.v === cond.value)
    value = (
      <RuleSelect
        className="rule-cond__fill"
        aria-label={`${prefix}, value`}
        value={cond.value}
        onChange={(e) => setValue(e.target.value)}
      >
        <option value="">Choose…</option>
        {!known && cond.value !== '' && <option value={cond.value}>(no longer available)</option>}
        {choices.map((o) => (
          <option key={o.v} value={o.v}>
            {o.l}
          </option>
        ))}
      </RuleSelect>
    )
  } else if (kind === 'date') {
    value = (
      <>
        <input
          type="date"
          className="rule-control"
          style={{ minHeight: h }}
          aria-label={`${prefix}, ${cond.op === 'between' ? 'from' : 'value'}`}
          value={cond.value}
          onChange={(e) => setValue(e.target.value)}
        />
        {cond.op === 'between' && (
          <>
            <span className="rule-cond__and" aria-hidden="true">
              and
            </span>
            <input
              type="date"
              className="rule-control"
              style={{ minHeight: h }}
              aria-label={`${prefix}, to`}
              value={cond.value2}
              onChange={(e) => setValue(e.target.value, true)}
            />
          </>
        )}
      </>
    )
  } else {
    value = chips(DAYS, 'days')
  }

  return (
    <li className={phone ? 'rule-cond rule-cond--phone' : 'rule-cond'}>
      <RuleSelect
        className="rule-cond__field"
        aria-label={`${prefix}, field`}
        value={cond.field}
        onChange={(e) => dispatch({ t: 'condField', g, c, v: e.target.value as FieldKey })}
      >
        {FIELD_ORDER.map((f) => (
          <option key={f} value={f}>
            {FIELDS[f].label}
          </option>
        ))}
      </RuleSelect>
      <RuleSelect
        className="rule-cond__op"
        aria-label={`${prefix}, operator`}
        value={cond.op}
        onChange={(e) => dispatch({ t: 'condOp', g, c, v: e.target.value as OpKey })}
      >
        {FIELDS[cond.field].ops.map((o) => (
          <option key={o} value={o}>
            {opLabel(cond.field, o)}
          </option>
        ))}
      </RuleSelect>
      <div className="rule-cond__value">{value}</div>
      {/* Always drawn, as the design's: the only row's is blocked, not absent,
          so the column never shifts when a second row arrives. */}
      <QuietIconButton
        className="rule-cond__remove"
        blocked={!removable}
        aria-label={`Remove ${prefix}`}
        onClick={() => dispatch({ t: 'removeCond', g, c })}
      />
      {hint}
    </li>
  )
}
